import {
  signalStore,
  withState,
  withMethods,
  patchState,
  withComputed,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import {
  SurveyListService,
} from '../survey-list-service/survey-list';
import { SurveyList,ListType} from '@hyper-inferno/survey-elements-core';

import { firstValueFrom } from 'rxjs';

export interface ListManagerState {
  lists: SurveyList[];
  activeListId: string | undefined;
  surveyId: number | undefined;
  loading: boolean;
  error: string | null;
  creatingList: boolean;
  searchTerm: string;
}

export const SurveyListStore = signalStore(
  { providedIn: 'root' },
  withState<ListManagerState>({
    lists: [],
    activeListId: undefined,
    surveyId: undefined,
    loading: false,
    error: null,
    creatingList: false,
    searchTerm: '',
  }),
  withMethods((store, listService = inject(SurveyListService)) => ({
    async loadLists(survey_id?: number) {
      // Use provided survey_id or fall back to store value
      const surveyId = survey_id || store.surveyId();

      if (!surveyId) {
        console.error('No survey ID provided');
        return;
      }

      patchState(store, { loading: true, error: null, surveyId });
      try {
        const lists = await firstValueFrom(listService.getList(surveyId));
        patchState(store, { lists, loading: false });
      } catch (error) {
        patchState(store, {
          error: 'Failed to load lists',
          loading: false,
        });
      }
    },

    updateSearchTerm(searchTerm: string) {
      patchState(store, { searchTerm });
    },

    setActiveList(listId: string | undefined) {
      patchState(store, { activeListId: listId });
    },

    async createListAndUpdateEntity(payload: {
      list_type: ListType;
      survey_id: number;
      es_entityName: string;
    }) {
      if (store.creatingList()) {
        return null;
      }
      patchState(store, { creatingList: true, error: null });
      try {
        const result = await firstValueFrom(
          listService.createList({
            list_type: payload.list_type,
            survey_id: payload.survey_id,
          }),
        );

        const listId = result.id;
        await this.loadLists(payload.survey_id);
        patchState(store, { creatingList: false });

        return { success: true, listId, list: result };
      } catch (error) {
        console.error('Error in createListAndUpdateEntity:', error);
        patchState(store, {
          error: 'Failed to create list',
          creatingList: false,
        });
        return { success: false, error };
      }
    },

    reset() {
      patchState(store, {
        lists: [],
        activeListId: undefined,
        surveyId: undefined,
        loading: false,
        error: null,
        creatingList: false,
      });
    },
    // Inside your SurveyListStore withMethods:
    updateListNameLocally(id: string, name: string) {
      patchState(store, (state) => ({
        lists: state.lists.map((l:SurveyList) => l.id === id ? { ...l, name } : l)
      }));
    },
  })),
  withComputed((store) => ({
    filteredLists: computed(() => {
      const lists = store.lists();
      const searchTerm = store.searchTerm()?.toLowerCase() || '';

      if (!searchTerm) {
        return lists;
      }

      return lists.filter((list) =>
        list.name?.toLowerCase().includes(searchTerm),
      );
    }),

    isLoading: computed(() => store.loading()),

    activeList: computed(() => {
      const lists = store.lists();
      const activeId = store.activeListId();
      return lists.find((list) => list.id === activeId);
    }),
  })),
);

// Export a type for the store
export type SurveyListStore = InstanceType<typeof SurveyListStore>;
